import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  AlertTriangle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const projectTypes = [
  'Public Utility Locating',
  'Private Utility Locating',
  'Ground-Penetrating Radar (GPR)',
  'Utility Mapping & GIS',
  'Damage Prevention Consulting',
  'Emergency Locate',
  'Other'
];

const urgencyLevels = [
  { value: 'standard', label: 'Standard (3-5 business days)' },
  { value: 'priority', label: 'Priority (1-2 business days)' },
  { value: 'emergency', label: 'Emergency (Same day / 24h)' },
];

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    project_type: '',
    project_location: '',
    project_description: '',
    urgency: 'standard',
    is_emergency: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'urgency' && value === 'emergency') {
      setFormData(prev => ({ ...prev, is_emergency: true }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/quote`, formData);
      setSubmitted(true);
      toast.success('Quote request submitted successfully!');
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to submit request. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-skytec-bg pt-20 min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md mx-auto px-4"
        >
          <div className="w-20 h-20 bg-skytec-accent/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-skytec-accent" />
          </div>
          <h2 className="font-heading text-3xl font-bold text-skytec-fg mb-4">REQUEST RECEIVED</h2>
          <p className="text-skytec-fg/60 mb-8">
            Thank you for your quote request! Our team will review your project details 
            and get back to you within 24 hours.
          </p>
          <div className="p-4 bg-skytec-surface rounded-xl border border-skytec-border mb-8">
            <p className="text-skytec-fg/80 text-sm">
              For immediate assistance, email us at:
            </p>
            <a href="mailto:info@usgllc811.com" className="text-xl text-skytec-secondary font-bold">
              info@usgllc811.com
            </a>
          </div>
          <Button 
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                project_type: '',
                project_location: '',
                project_description: '',
                urgency: 'standard',
                is_emergency: false
              });
            }}
            className="bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading"
            data-testid="submit-another-btn"
          >
            SUBMIT ANOTHER REQUEST
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="bg-skytec-bg pt-20">
      {/* Hero Section */}
      <section className="py-24 relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0 scan-grid opacity-20" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-skytec-primary font-mono text-sm tracking-widest uppercase">Get In Touch</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-skytec-fg mt-4 mb-6 uppercase tracking-tight">
              REQUEST A <span className="neon-text">QUOTE</span>
            </h1>
            <p className="text-skytec-fg/60 max-w-3xl mx-auto text-lg">
              Tell us about your project and we'll provide a detailed quote for our utility locating services.
              For emergencies, email us at info@usgllc811.com.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-1 space-y-8"
            >
              {/* Emergency Banner */}
              <div className="p-6 bg-skytec-secondary-dim rounded-xl border border-skytec-secondary/30">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-skytec-secondary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-6 h-6 text-skytec-secondary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-skytec-secondary mb-2">EMERGENCY CONTACT</h3>
                    <p className="text-skytec-fg/70 text-sm mb-3">
                      24/7 availability for urgent utility locating needs
                    </p>
                    <a 
                      href="mailto:info@usgllc811.com" 
                      className="text-xl text-skytec-secondary font-bold"
                      data-testid="emergency-email"
                    >
                      info@usgllc811.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div className="p-6 bg-skytec-surface rounded-xl border border-skytec-border">
                <h3 className="font-heading font-bold text-skytec-fg mb-6">CONTACT INFORMATION</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-skytec-primary-dim rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-skytec-primary" />
                    </div>
                    <div>
                      <div className="text-skytec-fg/60 text-sm">Email</div>
                      <a href="mailto:info@usgllc811.com" className="text-skytec-fg hover:text-skytec-secondary transition-colors">
                        info@usgllc811.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-skytec-primary-dim rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-skytec-primary" />
                    </div>
                    <div>
                      <div className="text-skytec-fg/60 text-sm">Headquarters</div>
                      <address className="text-skytec-fg not-italic">
                        5092 Southwest 73 Avenue<br />
                        Davie, FL 33314
                      </address>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-skytec-primary-dim rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-skytec-primary" />
                    </div>
                    <div>
                      <div className="text-skytec-fg/60 text-sm">Business Hours</div>
                      <div className="text-skytec-fg">
                        Mon - Fri: 7:00 AM - 6:00 PM<br />
                        <span className="text-skytec-secondary">24/7 Emergency Available</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="rounded-xl overflow-hidden border border-skytec-border h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3450.123!2d-80.2528!3d26.0629!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9a8c1f0e9a0a7%3A0x1234567890abcdef!2s5092%20SW%2073rd%20Ave%2C%20Davie%2C%20FL%2033314!5e0!3m2!1sen!2sus!4v1651234567890!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="USG Location"
                />
              </div>
            </motion.div>

            {/* Quote Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="lg:col-span-2"
            >
              <form onSubmit={handleSubmit} className="p-8 bg-skytec-surface rounded-2xl border border-skytec-border" data-testid="quote-form">
                <h2 className="font-heading text-2xl font-bold text-skytec-fg mb-8">PROJECT DETAILS</h2>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-skytec-fg">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="bg-skytec-bg border-skytec-border text-skytec-fg placeholder:text-skytec-fg/40"
                      placeholder="John Smith"
                      data-testid="input-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-skytec-fg">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="bg-skytec-bg border-skytec-border text-skytec-fg placeholder:text-skytec-fg/40"
                      placeholder="john@company.com"
                      data-testid="input-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-skytec-fg">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="bg-skytec-bg border-skytec-border text-skytec-fg placeholder:text-skytec-fg/40"
                      placeholder="(555) 123-4567"
                      data-testid="input-phone"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company" className="text-skytec-fg">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="bg-skytec-bg border-skytec-border text-skytec-fg placeholder:text-skytec-fg/40"
                      placeholder="ABC Construction"
                      data-testid="input-company"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-2">
                    <Label className="text-skytec-fg">Service Type *</Label>
                    <Select onValueChange={(value) => handleSelectChange('project_type', value)} required>
                      <SelectTrigger className="bg-skytec-bg border-skytec-border text-skytec-fg" data-testid="select-project-type">
                        <SelectValue placeholder="Select service type" />
                      </SelectTrigger>
                      <SelectContent className="bg-skytec-surface border-skytec-border">
                        {projectTypes.map((type) => (
                          <SelectItem key={type} value={type} className="text-skytec-fg hover:bg-skytec-primary-dim">
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-skytec-fg">Urgency Level</Label>
                    <Select 
                      onValueChange={(value) => handleSelectChange('urgency', value)} 
                      defaultValue="standard"
                    >
                      <SelectTrigger className="bg-skytec-bg border-skytec-border text-skytec-fg" data-testid="select-urgency">
                        <SelectValue placeholder="Select urgency" />
                      </SelectTrigger>
                      <SelectContent className="bg-skytec-surface border-skytec-border">
                        {urgencyLevels.map((level) => (
                          <SelectItem key={level.value} value={level.value} className="text-skytec-fg hover:bg-skytec-primary-dim">
                            {level.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2 mb-6">
                  <Label htmlFor="project_location" className="text-skytec-fg">Project Location *</Label>
                  <Input
                    id="project_location"
                    name="project_location"
                    value={formData.project_location}
                    onChange={handleChange}
                    required
                    className="bg-skytec-bg border-skytec-border text-skytec-fg placeholder:text-skytec-fg/40"
                    placeholder="City, State or full address"
                    data-testid="input-location"
                  />
                </div>

                <div className="space-y-2 mb-6">
                  <Label htmlFor="project_description" className="text-skytec-fg">Project Description *</Label>
                  <Textarea
                    id="project_description"
                    name="project_description"
                    value={formData.project_description}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="bg-skytec-bg border-skytec-border text-skytec-fg placeholder:text-skytec-fg/40 resize-none"
                    placeholder="Describe your project, including scope, timeline, and any specific requirements..."
                    data-testid="input-description"
                  />
                </div>

                <div className="flex items-start space-x-3 mb-8">
                  <Checkbox 
                    id="is_emergency"
                    checked={formData.is_emergency}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_emergency: checked }))}
                    className="border-skytec-border data-[state=checked]:bg-skytec-secondary data-[state=checked]:border-skytec-secondary"
                    data-testid="checkbox-emergency"
                  />
                  <div>
                    <Label htmlFor="is_emergency" className="text-skytec-fg cursor-pointer">
                      This is an emergency request
                    </Label>
                    <p className="text-skytec-fg/50 text-sm mt-1">
                      Check this box for immediate response. Our team will contact you within 1 hour.
                    </p>
                  </div>
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  size="lg"
                  className="w-full bg-skytec-secondary text-white hover:bg-skytec-secondary/90 font-heading font-bold text-lg"
                  data-testid="submit-quote-btn"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      SUBMITTING...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      SUBMIT REQUEST
                    </>
                  )}
                </Button>

                <p className="text-skytec-fg/40 text-sm text-center mt-4">
                  By submitting this form, you agree to be contacted by USG regarding your request.
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
